import { ConversationsTask } from 'Tasks/ConversationsTask/ConversationsTask'
import { IContainerStrategy } from '../ContainerStrategy'
import {
    AttributeDefinitionType,
    ContainerDataType,
    ContainerType,
    ConversationsDataType,
    TaskConfigType,
    UpdatedAttributesData,
    UpdatedQCAttributesData,
    UpdatedValueDataType,
} from 'Types/Tasks.types'
import { cloneDeep, isEmpty } from 'lodash'

export class ConversationContainerStrategy
    implements IContainerStrategy<ContainerType.Conversations, ConversationsDataType>
{
    containerContext?: {
        task: ConversationsTask
    }

    setContext(containerContext: { task: ConversationsTask }) {
        this.containerContext = containerContext
    }

    getEmptyDraftContainer(): ContainerDataType<
        ContainerType.Conversations,
        ConversationsDataType
    > {
        return {
            data: {
                type: ContainerType.Conversations,
                schema_version: 1,
                data: {
                    response: {
                        id: '',
                        is_complete: false,
                        version_id: '',
                        main_info: {
                            value: {
                                turns: [],
                                turn_annotations: [],
                            },
                            value_type: 'JSON',
                        },
                        extra_info: {},
                    },
                    quality_check: {
                        evaluation_on_response_version_id: null,
                    },
                },
            },
        }
    }

    getContainerFromOutputStart(
        outputStartContainer: ContainerDataType<ContainerType.Conversations, ConversationsDataType>
    ): ContainerDataType<ContainerType.Conversations, ConversationsDataType> {
        const draftContainer = cloneDeep(outputStartContainer)

        if (isEmpty(draftContainer.data.data.response.extra_info)) {
            draftContainer.data.data.response.extra_info = {}
        }

        draftContainer.data.data.quality_check.evaluation_on_response_version_id = {}
        draftContainer.decision = null

        return draftContainer
    }

    getModifiedDraftOutputContainer = (
        draftOutputContainer: ContainerDataType<ContainerType.Conversations, ConversationsDataType>,
        taskConfig: TaskConfigType<ContainerType.Conversations, ConversationsDataType>
    ) => {
        if (!this.containerContext?.task) {
            throw new Error('Task context not found')
        }
        const draftContainer = cloneDeep(draftOutputContainer)
        const data = draftContainer.data.data

        const extraInfo: UpdatedAttributesData = {}
        const qcAttributes: { [versionId: string]: UpdatedQCAttributesData } = {}

        const versionIds = Object.keys(data.quality_check.evaluation_on_response_version_id ?? {})

        taskConfig.assignment.task.attributes.forEach(attribute => {
            if (
                draftContainer.data.data.response.extra_info?.[attribute.name] &&
                attribute.type === AttributeDefinitionType.EXTRA_INFO
            ) {
                extraInfo[attribute.name] = draftContainer.data.data.response.extra_info?.[
                    attribute.name
                ] as UpdatedValueDataType<never>
            }

            versionIds.forEach(versionId => {
                if (
                    draftContainer.data.data.quality_check.evaluation_on_response_version_id?.[
                        versionId
                    ]?.[attribute.name] &&
                    attribute.type === AttributeDefinitionType.QUALITY_EVALUATION
                ) {
                    qcAttributes[versionId] = {
                        ...qcAttributes[versionId],
                        [attribute.name]:
                            draftContainer.data.data.quality_check
                                .evaluation_on_response_version_id[versionId][attribute.name],
                    } as UpdatedQCAttributesData
                }
            })
        })

        draftContainer.data.data.response.extra_info = extraInfo
        draftContainer.data.data.quality_check.evaluation_on_response_version_id = qcAttributes

        draftContainer.data.data.response.id = isEmpty(data.response.id)
            ? crypto.randomUUID()
            : data.response.id
        draftContainer.data.data.response.version_id = isEmpty(data.response.id)
            ? null
            : data.response.version_id

        return draftContainer
    }
}
